import axios, { AxiosError } from 'axios';
import { useAuthStore } from '../stores/movieStore';

const API_BASE = '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response.data,
  (error: AxiosError<{ success?: boolean; error?: string }>) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    const data = error.response?.data;
    return Promise.reject(data?.error || error.message || '请求失败');
  }
);

export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (username: string, email: string, password: string) =>
    api.post('/auth/register', { username, email, password }),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data: { username?: string; avatar?: string }) =>
    api.put('/auth/profile', data),
};

export const movieAPI = {
  search: (query: string, page = 1) =>
    api.get('/movies/search', { params: { q: query, page } }),
  getPopular: (page = 1) => api.get('/movies/popular', { params: { page } }),
  getLatest: (page = 1) => api.get('/movies/latest', { params: { page } }),
  getDetail: (id: string) => api.get(`/movies/${encodeURIComponent(id)}`),
  getRecommendations: (id: string) => api.get(`/movies/${encodeURIComponent(id)}/recommendations`),
  getSources: () => api.get('/movies/sources/list'),
};

export const userAPI = {
  getFavorites: () => api.get('/user/favorites'),
  addFavorite: (movie: any) => api.post('/user/favorites', {
    movieId: movie.id,
    title: movie.title,
    poster: movie.poster,
    year: movie.year,
    rating: movie.rating,
    genre: movie.genre,
    description: movie.description,
  }),
  removeFavorite: (movieId: string) => api.delete(`/user/favorites/${encodeURIComponent(movieId)}`),
  getHistory: () => api.get('/user/history'),
  clearHistory: () => api.delete('/user/history'),
  updateProgress: (movieId: string, progress: number, sourceId: string, movie?: any) =>
    api.put('/user/history', {
      movieId,
      progress,
      sourceId,
      title: movie?.title,
      poster: movie?.poster,
      year: movie?.year,
      rating: movie?.rating,
      genre: movie?.genre,
      description: movie?.description,
    }),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getMovies: (page = 1) => api.get('/admin/movies', { params: { page } }),
  addMovie: (data: any) => api.post('/admin/movies', data),
  updateMovie: (id: string, data: any) => api.put(`/admin/movies/${id}`, data),
  deleteMovie: (id: string) => api.delete(`/admin/movies/${id}`),
  getSources: () => api.get('/admin/sources'),
  addSource: (data: any) => api.post('/admin/sources', data),
  updateSource: (id: string, data: any) => api.put(`/admin/sources/${id}`, data),
  deleteSource: (id: string) => api.delete(`/admin/sources/${id}`),
};

export default api;
